// Tour module API service
import { apiService } from '../../../shared/services/api';
import { Tour, TourForm } from '../types';
import { Museum } from '../../../shared/types';

export class TourService {
  private baseUrl = '/tours';

  // Tours CRUD
  async getTours(filters?: { museum_code?: string; is_active?: boolean }): Promise<Tour[]> {
    const params = new URLSearchParams();
    if (filters?.museum_code) params.append('museum_code', filters.museum_code);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());

    const url = `${this.baseUrl}/tours/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiService.getPaginated<Tour>(url);
    return response.results;
  }

  async getTour(id: number): Promise<Tour> {
    return apiService.get<Tour>(`${this.baseUrl}/tours/${id}/`);
  }

  async createTour(data: TourForm): Promise<Tour> {
    return apiService.post<Tour>(`${this.baseUrl}/tours/`, data);
  }

  async updateTour(id: number, data: Partial<TourForm>): Promise<Tour> {
    return apiService.put<Tour>(`${this.baseUrl}/tours/${id}/`, data);
  }

  async deleteTour(id: number): Promise<void> {
    await apiService.delete(`${this.baseUrl}/tours/${id}/`);
  }

  // Related data
  async getMuseums(): Promise<Museum[]> {
    const response = await apiService.getPaginated<Museum>('/museums/');
    return response.results;
  }

  // Waypoints for tour (cached waypoints)
  async getWaypointsByMuseum(museumCode: string): Promise<any[]> {
    const response = await apiService.getPaginated('/tours/waypoints/', { museum_code: museumCode });
    return response.results;
  }

  // Utility methods
  async getToursByMuseum(museumCode: string): Promise<Tour[]> {
    return this.getTours({ museum_code: museumCode });
  }

  async getActiveTours(museumCode?: string): Promise<Tour[]> {
    return this.getTours({ is_active: true, museum_code: museumCode });
  }

  async getNextTourId(museumCode: string): Promise<number> {
    const resp = await apiService.get<{ next_id: number }>(
      `${this.baseUrl}/tours/next-id/?museum_code=${encodeURIComponent(museumCode)}`,
    );
    return resp.next_id;
  }

  /**
   * Generate tour YAML URL based on tour data, museum code, and level
   */
  getTourYamlUrl(tour: Tour, museumCode: string, museumLevel: string = '1'): string {
    // Get AWS S3 bucket name from environment or use default
    const bucketName = process.env.REACT_APP_AWS_S3_BUCKET_NAME || 'nhb-robot-dashboard';
    const region = process.env.REACT_APP_AWS_S3_REGION_NAME || 'ap-southeast-1';
    const tourId = tour.tour_id || tour.id;
    const filename = `${museumCode.toLowerCase()}-${tourId}-${museumLevel}.yaml`;
    const s3Key = `yaml/tours/${filename}`;
    return `https://${bucketName}.s3.${region}.amazonaws.com/${s3Key}`;
  }

  /**
   * Install tours to a robot
   */
  async installTours(
    robotId: string,
    museumCode: string,
    museumLevel: string,
    tourUrls: string[],
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await apiService.post<{ success: boolean; data?: any; error?: string }>(
        `${this.baseUrl}/install/`,
        {
          robot_id: robotId,
          museum_code: museumCode,
          museum_level: museumLevel,
          tour_urls: tourUrls,
        },
      );
      return response;
    } catch (error: any) {
      console.error('Error installing tours:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to install tours',
      };
    }
  }

  /**
   * Activate tours on a robot
   */
  async activateTours(
    robotId: string,
    museumCode: string,
    museumLevel: string,
    tourUrls: string[],
  ): Promise<{ robot_id: string; museum_code: string; museum_level: string; tour_urls: string[] }> {
    return apiService.post<{ robot_id: string; museum_code: string; museum_level: string; tour_urls: string[] }>(
      `${this.baseUrl}/activate/`,
      {
        robot_id: robotId,
        museum_code: museumCode,
        museum_level: museumLevel,
        tour_urls: tourUrls,
      },
    );
  }

  /**
   * Get installed tours for a robot
   */
  async getInstalledTours(
    robotId: string,
    museumCode?: string,
    museumLevel?: string,
  ): Promise<{ robot_id: string; museum_code: string; museum_level: string; tour_urls: string[] }> {
    const params = new URLSearchParams();
    if (museumCode) params.append('museum_code', museumCode);
    if (museumLevel) params.append('museum_level', museumLevel);

    const url = `/robots/${robotId}/tours/installed/${params.toString() ? `?${params.toString()}` : ''}`;
    return apiService.get<{ robot_id: string; museum_code: string; museum_level: string; tour_urls: string[] }>(url);
  }
}

// Singleton instance
export const tourService = new TourService();
